import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, FileText, Globe, Menu, Save, X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const MENU_POSITIONS = [
  { value: 'header', label: 'Header Menu' },
  { value: 'footer', label: 'Footer Menu' },
  { value: 'sidebar', label: 'Sidebar' },
];

const AdminCustomPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    is_published: true,
    show_in_menu: false,
    menu_position: 'footer',
    order: 0,
    meta_description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await api.get('/admin/pages');
      setPages(res.data);
    } catch (error) {
      console.error('Failed to fetch pages:', error);
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingPage) {
        await api.put(`/admin/pages/${editingPage.id}`, formData);
        toast.success('Page updated');
      } else {
        await api.post('/admin/pages', formData);
        toast.success('Page created');
      }
      setShowForm(false);
      setEditingPage(null);
      resetForm();
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save page');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title,
      slug: page.slug,
      content: page.content,
      is_published: page.is_published,
      show_in_menu: page.show_in_menu,
      menu_position: page.menu_position || 'footer',
      order: page.order || 0,
      meta_description: page.meta_description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await api.delete(`/admin/pages/${pageId}`);
      toast.success('Page deleted');
      fetchPages();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const togglePublish = async (page) => {
    try {
      await api.put(`/admin/pages/${page.id}`, { is_published: !page.is_published });
      toast.success(page.is_published ? 'Page unpublished' : 'Page published');
      fetchPages();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      is_published: true,
      show_in_menu: false,
      menu_position: 'footer',
      order: 0,
      meta_description: ''
    });
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .trim();
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-custom-pages">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold flex items-center gap-3">
            <FileText className="w-10 h-10 text-primary" />
            Custom Pages
          </h1>
          <button
            onClick={() => { resetForm(); setEditingPage(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="create-page-btn"
          >
            <Plus className="w-5 h-5" />
            Create Page
          </button>
        </div>

        {/* Page Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1a1a] rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">
                  {editingPage ? 'Edit Page' : 'Create New Page'}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1">Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => {
                        setFormData({ 
                          ...formData, 
                          title: e.target.value,
                          slug: formData.slug || generateSlug(e.target.value)
                        });
                      }}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                      placeholder="Page Title"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">URL Slug *</label>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-sm">/page/</span>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
                        className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                        placeholder="url-slug"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-1">Content (HTML supported) *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl font-mono text-sm"
                    rows={12}
                    placeholder="<h1>Your Content Here</h1>\n<p>Write your page content...</p>"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Meta Description (SEO)</label>
                  <input
                    type="text"
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="Brief description for search engines..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="is_published"
                      checked={formData.is_published}
                      onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                      className="w-5 h-5"
                    />
                    <label htmlFor="is_published" className="flex items-center gap-2">
                      <Globe className="w-4 h-4" /> Published
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="show_in_menu"
                      checked={formData.show_in_menu}
                      onChange={(e) => setFormData({ ...formData, show_in_menu: e.target.checked })}
                      className="w-5 h-5"
                    />
                    <label htmlFor="show_in_menu" className="flex items-center gap-2">
                      <Menu className="w-4 h-4" /> Show in Menu
                    </label>
                  </div>
                  {formData.show_in_menu && (
                    <div>
                      <select
                        value={formData.menu_position}
                        onChange={(e) => setFormData({ ...formData, menu_position: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                      >
                        {MENU_POSITIONS.map(pos => (
                          <option key={pos.value} value={pos.value}>{pos.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 px-4 py-3 bg-white/10 rounded-xl hover:bg-white/20"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 btn-primary flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    {submitting ? 'Saving...' : 'Save Page'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Pages List */}
        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : pages.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No custom pages yet</p>
            <p className="text-sm text-muted-foreground mt-2">Create pages like Terms & Conditions, Privacy Policy, About Us, etc.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pages.map((page) => (
              <div key={page.id} className="glass-card p-4" data-testid={`page-${page.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-lg">{page.title}</h3>
                      {page.is_published ? (
                        <span className="text-xs bg-green-500/20 text-green-500 px-2 py-1 rounded">Published</span>
                      ) : (
                        <span className="text-xs bg-yellow-500/20 text-yellow-500 px-2 py-1 rounded">Draft</span>
                      )}
                      {page.show_in_menu && (
                        <span className="text-xs bg-blue-500/20 text-blue-500 px-2 py-1 rounded">
                          {page.menu_position} menu
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">/page/{page.slug}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => togglePublish(page)}
                      className={`p-2 rounded-lg ${page.is_published ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}
                      title={page.is_published ? 'Unpublish' : 'Publish'}
                    >
                      {page.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleEdit(page)}
                      className="p-2 bg-blue-500/20 text-blue-500 rounded-lg hover:bg-blue-500/30"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(page.id)}
                      className="p-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCustomPages;
